import { FlexView, Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Table, TableCell } from 'comps';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import Calendar from './calendar';
import { useGetAllEvalSpecialistAvailability } from 'actions/eval-reporting';
import {
  ErrorLine,
  FilterRow,
  MonoText,
  PageHeader,
  RangePicker,
  RegionPicker,
  TimezonePicker,
} from '../common/filters';
import {
  BLOCK_STATUSES,
  IST_ZONE,
  RANGE_TYPES,
  TIMEZONE_OPTIONS,
  currentMonthValue,
  rangeError,
  rangeQuery,
  epochDateKeyInZone,
  formatDateColumnInZone,
  formatEpochTimeInZone,
} from '../common/constants';

const Container = styled(FlexView)`
  padding: 0 16px;
  width: 100%;
`;
const TableWrapper = styled(Row)`
  margin-top: 16px;
  width: 100%;
`;
const BlockList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Block = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${({ color }) => color};
  border: 1px solid ${({ color }) => color};
  background: ${({ background }) => background};
`;
const Legend = styled(Row)`
  gap: 14px;
  align-items: center;
`;
const Swatch = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${COLORS.GRAY};

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 2px;
    border: 1px solid ${({ color }) => color};
    background: ${({ background }) => background};
  }
`;
const OnlyOffered = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${COLORS.GRAY};
  cursor: pointer;
`;
const Count = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ color }) => color};
`;

// A block belongs to the day it falls on in the viewer's zone, not in IST.
const blocksByDate = (specialist, zone) => {
  const grouped = {};

  (specialist.blocks || []).forEach(block => {
    const key = epochDateKeyInZone(block.start, zone) || block.date;

    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(block);
  });

  return grouped;
};

const Availability = () => {
  const [onlyOffered, setOnlyOffered] = useState(true);
  const [region, setRegion] = useState(null);
  const [zone, setZone] = useState(IST_ZONE);
  const [isCalendar, setIsCalendar] = useState(false);
  const [range, setRange] = useState({
    rangeType: RANGE_TYPES.week,
    month: currentMonthValue,
    startDate: '',
    endDate: '',
  });

  const {
    data,
    getAll: getAvailability,
    isProcessing: isLoading,
    error: availabilityError,
  } = useGetAllEvalSpecialistAvailability();

  const isRangeInvalid =
    range.rangeType === RANGE_TYPES.custom &&
    Boolean(rangeError(range.startDate, range.endDate));

  const fetchAvailability = useCallback(() => {
    if (isRangeInvalid) return;
    getAvailability({ ...rangeQuery(range, zone), ...(region ? { region } : {}) });
  }, [getAvailability, range, zone, region, isRangeInvalid]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const totals = data?.totals || {};
  const all = data?.specialists || [];
  const specialists = onlyOffered ? all.filter(s => (s.blocks || []).length) : all;
  const specialistCount = specialists.length;
  // Straight from the response, which resolved these in the zone we asked for.
  const dates = data?.dates || [];
  const zoneLabel =
    (TIMEZONE_OPTIONS.find(option => option.value === zone) || {}).text || zone;

  return (
    <Container>
      <PageHeader title="Specialist Availability" />
      <FilterRow justifyContent="space-between" alignItems="center">
        <Row alignItems="center" style={{ gap: '18px' }}>
          <RangePicker value={range} onChange={setRange} />
          <RegionPicker value={region} onChange={setRegion} />
          <OnlyOffered>
            <input
              type="checkbox"
              checked={onlyOffered}
              onChange={event => setOnlyOffered(event.target.checked)}
            />
            Only specialists offering time
          </OnlyOffered>
          <Legend>
            {Object.values(BLOCK_STATUSES).map(status => (
              <Swatch
                key={status.key}
                color={status.color}
                background={status.background}
              >
                {status.label}
              </Swatch>
            ))}
          </Legend>
        </Row>
        <Row alignItems="center" style={{ gap: '12px' }}>
          <Text variant="body3" color={COLORS.GRAY}>
            {dates.length ? `${dates.length} days, ${zoneLabel}. ` : ''}
            {`${specialistCount} specialist${specialistCount === 1 ? '' : 's'} · ${totals.available ??
              0} available · ${totals.occupied ?? 0} occupied · ${totals.paused ??
              0} paused`}
          </Text>
          <Button.Group size="tiny">
            <Button
              basic={isCalendar}
              color={isCalendar ? null : 'black'}
              onClick={() => setIsCalendar(false)}
            >
              Table
            </Button>
            <Button
              basic={!isCalendar}
              color={isCalendar ? 'black' : null}
              onClick={() => setIsCalendar(true)}
            >
              Calendar
            </Button>
          </Button.Group>
          <TimezonePicker value={zone} onChange={setZone} region={region} />
        </Row>
      </FilterRow>

      <ErrorLine error={availabilityError} />

      {isCalendar ? (
        <Calendar
          specialists={specialists}
          dates={dates}
          zone={zone}
          zoneLabel={zoneLabel}
        />
      ) : (
        <TableWrapper>
          <Table
            headerCell={[
              { title: 'Specialist' },
              { title: 'Specialist ID' },
              { title: 'Free' },
              ...dates.map(date => ({ title: formatDateColumnInZone(date, zone) })),
            ]}
            dataLength={specialists.length}
            havePagination
            showInfo={false}
            showLoader={isLoading}
            flexBasis="100%"
            noDataMessage="No eval specialist availability to show."
            scrollBodyHeight={isLoading || !specialists.length ? '532px' : 'calc(100vh - 210px)'}
          >
            {specialists.map(specialist => {
              const grouped = blocksByDate(specialist, zone);

              return (
                <TableRow key={specialist.teacher_id}>
                  <TableCell width={3} textTransform="none">
                    {specialist.teacher_name || '-'}
                  </TableCell>
                  <TableCell width={4} textTransform="none">
                    <MonoText>{specialist.teacher_id}</MonoText>
                  </TableCell>
                  <TableCell width={2} textTransform="none">
                    <Count color={COLORS.DARK_GREEN}>{specialist.available ?? 0}</Count>
                    {' / '}
                    <Count color={COLORS.GRAY}>
                      {(specialist.available ?? 0) +
                        (specialist.occupied ?? 0) +
                        (specialist.paused ?? 0)}
                    </Count>
                  </TableCell>
                  {dates.map(date => (
                    <TableCell key={date} width={2} textTransform="none">
                      {(grouped[date] || []).length ? (
                        <BlockList>
                          {grouped[date].map(block => {
                            const status =
                              BLOCK_STATUSES[block.status] || BLOCK_STATUSES.available;

                            return (
                              <Block
                                key={block.start}
                                color={status.color}
                                background={status.background}
                                title={
                                  block.student_name
                                    ? `${status.label} — ${block.student_name}`
                                    : status.label
                                }
                              >
                                {formatEpochTimeInZone(block.start, zone)}–
                                {formatEpochTimeInZone(block.end, zone)}
                              </Block>
                            );
                          })}
                        </BlockList>
                      ) : (
                        <Text variant="body3" color={COLORS.GRAY}>
                          —
                        </Text>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </Table>
        </TableWrapper>
      )}
    </Container>
  );
};

export default Availability;
